import { supabase } from '@sixam/supabase/client';
import type { Database, Json } from '@sixam/shared/database.types';
import type { ParsedGpxRoute, RouteBounds, RouteLineString } from '@sixam/shared/route-core';

type RouteRow = Database['public']['Tables']['routes']['Row'];
type RouteVersionRow = Database['public']['Tables']['route_versions']['Row'];

export type RouteVersion = Omit<RouteVersionRow, 'bounds' | 'geometry'> &
  Readonly<{
    bounds: RouteBounds;
    geometry: RouteLineString;
  }>;

export type RouteLibraryItem = Readonly<{
  route: Pick<
    RouteRow,
    'description' | 'id' | 'name' | 'share_enabled' | 'share_token' | 'tenant_id'
  >;
  version: RouteVersion;
}>;

type RouteVersionWithRoute = RouteVersionRow & {
  routes: Pick<
    RouteRow,
    'description' | 'id' | 'name' | 'share_enabled' | 'share_token' | 'tenant_id'
  >;
};

function toRouteLibraryItem(row: RouteVersionWithRoute): RouteLibraryItem {
  return {
    route: row.routes,
    version: {
      ...row,
      bounds: row.bounds as unknown as RouteBounds,
      geometry: row.geometry as unknown as RouteLineString,
    },
  };
}

export async function fetchTenantRoutes(tenantId: string): Promise<RouteLibraryItem[]> {
  const result = await supabase
    .from('route_versions')
    .select('*, routes!inner(id, name, description, share_enabled, share_token, tenant_id)')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });

  if (result.error) throw result.error;
  return (result.data as RouteVersionWithRoute[]).map(toRouteLibraryItem);
}

export async function fetchRouteVersion(id: string): Promise<RouteLibraryItem | null> {
  const result = await supabase
    .from('route_versions')
    .select('*, routes!inner(id, name, description, share_enabled, share_token, tenant_id)')
    .eq('id', id)
    .maybeSingle();

  if (result.error) throw result.error;
  return result.data ? toRouteLibraryItem(result.data as RouteVersionWithRoute) : null;
}

export async function createGpxRoute({
  description,
  fileName,
  name,
  parsed,
  tenantId,
}: Readonly<{
  description?: string;
  fileName: string;
  name: string;
  parsed: ParsedGpxRoute;
  tenantId: string;
}>): Promise<string> {
  const start = parsed.geometry.coordinates[0];
  const end = parsed.geometry.coordinates.at(-1)!;
  const result = await supabase.rpc('create_route_with_version', {
    p_bounds: parsed.bounds as unknown as Json,
    p_description: description?.trim() ?? '',
    p_distance_m: parsed.distanceM,
    p_elevation_gain_m: parsed.elevationGainM as number,
    p_end_lat: end[1],
    p_end_lng: end[0],
    p_geometry: parsed.geometry as unknown as Json,
    p_name: name.trim(),
    p_source: 'gpx',
    p_source_metadata: { file_name: fileName },
    p_start_lat: start[1],
    p_start_lng: start[0],
    p_tenant_id: tenantId,
  });

  if (result.error) throw result.error;
  return result.data;
}
